import type { Uuid } from "spacetimedb";
import { useState } from "react";
import { PlaidEmbeddedLink } from "react-plaid-link";

import {
  Drawer,
  DrawerBody,
  DrawerCloseIcon,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { Skeleton } from "@/web/components/primitives/skeleton";
import { Spinner } from "@/web/components/primitives/spinner";
import { toaster } from "@/web/components/primitives/toast";
import { useMountEffect } from "@/web/lib/effects";
import { ErrorHandler } from "@/web/lib/errors";
import { useProcedureMutation } from "@/web/lib/tanstack";
import { procedures } from "@tycho/web-bindings";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";
import { usePostHog } from "@posthog/react";
import { PosthogEvents } from "@/web/lib/posthog-events";

type ConnectBankAccountProps = {
  /** Underwritable entity that will own the resulting plaid item. */
  underwritableEntity: Uuid;
};

function ConnectBankAccountAction({
  underwritableEntity,
  onSkip,
  onComplete,
}: ConnectBankAccountProps & SmbActionCoreProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <ActionCard category="budget">
        <ActionCard.Header>
          <ActionCard.Badge>Budget</ActionCard.Badge>
        </ActionCard.Header>
        <ActionCard.Main>
          <ActionCard.Title>Connect bank account</ActionCard.Title>
          <ActionCard.Description>
            Link the business's primary account so we can verify cash flow automatically.
          </ActionCard.Description>
        </ActionCard.Main>
        <ActionCard.Footer>
          <ActionCard.SkipButton onSkip={onSkip} />
          <ActionCard.PrimaryButton onClick={() => setOpen(true)}>Connect</ActionCard.PrimaryButton>
        </ActionCard.Footer>
      </ActionCard>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Connect bank account</DrawerTitle>
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody className="flex flex-col">
            {/*
              The embedded flow is only mounted while the drawer is open (vaul
              portals DrawerContent on open/close), so we fetch a fresh link
              token each session and don't need any extra gating here.
            */}
            <PlaidEmbeddedFlow
              underwritableEntity={underwritableEntity}
              onComplete={() => {
                setOpen(false);
                onComplete();
              }}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}

function PlaidEmbeddedFlow({
  underwritableEntity,
  onComplete,
}: {
  underwritableEntity: Uuid;
  onComplete: () => void;
}) {
  const { mutate: createLinkToken, data: linkTokenData } = useProcedureMutation(
    procedures.createLinkToken,
    {
      onError: ErrorHandler.default,
    },
  );

  useMountEffect(() => {
    createLinkToken();
  });

  if (!linkTokenData) {
    return <Skeleton className="h-full min-h-96 w-full" />;
  }

  return (
    <PlaidEmbeddedFlowInner
      linkToken={linkTokenData.linkToken}
      underwritableEntity={underwritableEntity}
      onComplete={onComplete}
    />
  );
}

function PlaidEmbeddedFlowInner({
  linkToken,
  underwritableEntity,
  onComplete,
}: {
  linkToken: string;
  underwritableEntity: Uuid;
  onComplete: () => void;
}) {
  const posthog = usePostHog();
  const connectPlaidMutation = useProcedureMutation(procedures.connectPlaid, {
    onSuccess: () => {
      PosthogEvents.BankAccountConnected(posthog, underwritableEntity);
      toaster({ message: "Bank account connected", variant: "success" });
      onComplete();
    },
    onError: ErrorHandler.default,
  });

  return (
    <div className="relative flex h-full min-h-96 w-full flex-col">
      <PlaidEmbeddedLink
        className="h-full min-h-96 w-full"
        token={linkToken}
        onSuccess={(publicToken) => {
          connectPlaidMutation.mutate({
            underwritableEntity,
            plaidPublicToken: publicToken,
          });
        }}
      />
      {connectPlaidMutation.isPending ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/80">
          <Spinner size="medium" />
          <p className="text-sm text-muted-foreground">Finalizing your connection…</p>
        </div>
      ) : null}
    </div>
  );
}

export function createConnectBankAccountAction(
  props: ConnectBankAccountProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = "connect-bank-account", canSkip = true, ...rest } = props;
  return {
    id,
    category: "budget",
    canSkip,
    render: (core) => <ConnectBankAccountAction {...rest} {...core} />,
  };
}

// ── Engine rules ─────────────────────────────────────────────────────────────
import type { BankingConnection } from "@tycho/web-bindings/types";
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import { createConnectBankAccountCompleted } from "@/web/routes/_protected/smb/-dashboard/-completedActions/ConnectBankAccountCompleted";

export const connectBankRule = CardRulesEngine.singletonRule({
  id: "connect-bank",
  priority: 70,
  when: CardRulesEngine.and(
    CardRulesEngine.missingConnection("banking"),
    CardRulesEngine.not(CardRulesEngine.hasNoScheduledCalls),
  ),
  build: (d) => createConnectBankAccountAction({ underwritableEntity: d.underwritableEntityId }),
});

// Per-connection: one completed card per linked bank account, regardless of
// whether the active rule is still firing (e.g. if we ever support adding a
// second bank).
export const connectBankCompletedRule = CardRulesEngine.forEachCompletedRule<BankingConnection>({
  id: "connect-bank:done",
  forEach: CardRulesEngine.eachConnection("banking"),
  when: CardRulesEngine.always,
  build: (_, connection) => createConnectBankAccountCompleted({ connection }),
});
